import { Injectable } from '@angular/core';
import { createSecureStorageService, SecureStorageModel, SecureStorageService } from '@paymash/capacitor-secure-keys-storage-plugin';
import { NativeOnlyClass } from '@shared/decorators/native-only-class.decor';
import { Device, DeviceInfo } from '@core/capacitor-plugins/capacitor-plugins.class';
import { AdyenDeviceModel } from '@shared/constants';
import { MANUFACTURER_TYPES } from '@shared/constants/manufacturer-types';
import { LogService } from '../logger';

interface PosDeviceSettings {
  deviceIdentifier: string;
  [key: string]: unknown;
}

@Injectable()
@NativeOnlyClass()
export class SecureStorageWrapperService {
  private model: SecureStorageModel<PosDeviceSettings> = {
    // `key` is BOTH the legacy localStorage key (source for migration)
    // AND the key used in Secure Storage afterwards.
    deviceIdentifier: { key: 'deviceIdentifier', type: 'string', removeOnReinstall: true },
  };
  private secureStorageInstance: SecureStorageService<PosDeviceSettings>;
  private isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private readonly logger = this.logService.createLogger('SecureStorageWrapperService');

  constructor(private logService: LogService) {
    this.createStorage();
  }

  public get isPluginWasInitialed(): boolean {
    return !!this.secureStorageInstance && this.isInitialized;
  }

  // Idempotent: concurrent/repeated callers await the same underlying initialization.
  public async init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.runInit();
    }
    await this.initPromise;
  }

  private async runInit(): Promise<void> {
    try {
      if (await this.isHardwareTerminal()) {
        this.logger.debug('SecureStorageWrapperService init skipped on hardware terminal');
        return;
      }
      await this.secureStorageInstance.initialize();
      this.isInitialized = true;
    } catch (error) {
      this.isInitialized = false;
      // Allow a later retry (e.g. after a transient native failure).
      this.initPromise = null;
      this.logger.error(error, 'SecureStorageWrapperService init Error');
    }
  }

  public get<T>(propertyName: string): T {
    return this.secureStorageInstance.getValue(propertyName) as T;
  }

  public async set(propertyName: string, value: unknown): Promise<void> {
    await this.secureStorageInstance.setValue(propertyName, value);
  }

  public remove(propertyName: string) {
    this.secureStorageInstance.removeValue(propertyName);
  }

  public async hasKey(propertyName: string): Promise<boolean> {
    return await this.secureStorageInstance.hasValue(propertyName);
  }

  private createStorage() {
    this.secureStorageInstance = createSecureStorageService<PosDeviceSettings>(this.model);
  }

  private async isHardwareTerminal(): Promise<boolean> {
    const info: DeviceInfo | null = await Device.getInfo().catch(() => null);
    const manufacturer: string = info?.manufacturer || '';
    const model: string = info?.model || '';

    const isAdyen: boolean =
      [MANUFACTURER_TYPES.QUALCOMM, MANUFACTURER_TYPES.CASTLES].includes(manufacturer) &&
      Object.values(AdyenDeviceModel).some((adyenModel: string): boolean =>
        model.toLocaleLowerCase().includes(adyenModel.toLocaleLowerCase())
      );
    const isMyPos: boolean =
      [MANUFACTURER_TYPES.XGD, MANUFACTURER_TYPES.PAX, MANUFACTURER_TYPES.SPRD, MANUFACTURER_TYPES.LEPHONE].includes(manufacturer) ||
      (manufacturer === MANUFACTURER_TYPES.QUALCOMM && model.toUpperCase() === 'F20');

    const isSunmi: boolean = manufacturer.toLowerCase() === 'sunmi';

    return isAdyen || isMyPos || isSunmi;
  }
}
